import { useEffect, useRef, useState } from 'react';

/**
 * Eases a displayed value toward `target` with separate attack / release time constants (ms), so a meter that
 * receives a sample every 250 ms (cloud) or 40 ms (local) glides instead of stepping. Without
 * requestAnimationFrame (tests) the value is shown as is.
 */
export function useSmoothed(target: number, attackMs = 60, releaseMs = 450): number {
  const [shown, setShown] = useState(target);
  const r = useRef({ shown: target, target, raf: 0, last: 0 });
  r.current.target = target;
  useEffect(() => {
    const s = r.current;
    if (typeof requestAnimationFrame !== 'function') {
      s.shown = target;
      setShown(target);
      return;
    }
    const step = (t: number) => {
      const dt = s.last ? Math.min(100, t - s.last) : 16;
      s.last = t;
      const diff = s.target - s.shown;
      const k = 1 - Math.exp(-dt / Math.max(1, diff > 0 ? attackMs : releaseMs));
      s.shown += diff * k;
      if (Math.abs(s.target - s.shown) < 0.05) {
        s.shown = s.target;
        s.raf = 0;
        s.last = 0;
        setShown(s.shown);
        return;
      }
      setShown(s.shown);
      s.raf = requestAnimationFrame(step);
    };
    if (s.raf) cancelAnimationFrame(s.raf);
    s.last = 0;
    s.raf = requestAnimationFrame(step);
    return () => {
      if (s.raf) cancelAnimationFrame(s.raf);
      s.raf = 0;
    };
  }, [target, attackMs, releaseMs]);
  return shown;
}
